/**
 * Response-shape guards, in one place, with their evidence.
 *
 * Every guarded route asserts a few top-level keys before the client reads
 * the body, so a contract drift fails loudly instead of rendering nonsense.
 * That only works if the named keys are the ones the API actually returns.
 * Four of them were not: the guards were written from what the response was
 * assumed to look like, and each one failed a healthy response shut.
 *
 * So the keys live here beside OBSERVED, which records the exact top-level
 * keys seen from production, and `contract.test.ts` fails if any guard names
 * a key its route does not return. A guard is a claim about the wire; this
 * file is where that claim is checked.
 */

/** Keys `req()` requires before the client may read a route's body. */
export const RESPONSE_KEYS = {
  accountBuckets: ['matchedCount', 'nextCursor', 'items'],
  contentReaderGrants: ['items'],
  reconstructionRequests: ['items'],
  syncDrift: ['items'],
  companyDashboard: ['companyId'],
  analysisHistory: ['items'],
  analysisRun: ['run', 'tools'],
  analysisEvents: ['events'],
  analysisLogs: ['logs'],
  analysisReport: ['run', 'toolStates'],
  reportCharts: ['items'],
  knowledgeGraph: ['runId'],
  knowledgeSubgraph: ['nodes', 'edges'],
  toolset: ['entries'],
  attributionObjects: ['items'],
  attributionStray: ['items'],
  attributionRules: ['items'],
  bucketChart: ['bucketId', 'series', 'availability'],
  walkthrough: ['bucketId', 'tools'],
  toolView: ['bucketId', 'tool'],
  toolRows: ['items'],
  graphProjection: ['nodes', 'edges'],
  graphNode: ['node'],
  record: ['recordRef'],
  search: ['items'],
  // Verified against the deployed route on capway 2026-08-20: the envelope
  // carries `items` and `basis`, and `basis` is a sentence the page prints.
  sourceCollections: ['items', 'basis'],
  observedBucketFolders: ['bucketName', 'items', 'basis'],
  evidenceBatch: ['items'],
  finding: ['findingId'],
  packagingRun: ['id', 'state'],
} as const satisfies Record<string, readonly string[]>;

export type GuardedRoute = keyof typeof RESPONSE_KEYS;

/**
 * Top-level keys observed from api.poseidon.withmeridian.ai on 2026-08-03.
 * These are transcriptions of real successful responses, not of the spec:
 * several of these routes answer `AnyDataEnvelope`, so the spec cannot be
 * the authority for their shape.
 */
export const OBSERVED: Partial<Record<GuardedRoute, readonly string[]>> = {
  // Re-transcribed 2026-08-22: the envelope carries `matchedCount` and
  // `nextCursor` beside `items`, and this account answers matchedCount 251
  // with fifty on the first page. Guarding on `items` alone is how a page of
  // fifty was rendered as the whole account.
  accountBuckets: ['matchedCount', 'nextCursor', 'items'],
  // Transcribed from production on 2026-08-21, signed in: `aiinsurance26`
  // answers five top-level folders, every prefix ending in a slash and every
  // one `isPresent`, last observed 11:51 that morning.
  observedBucketFolders: ['bucketName', 'items', 'basis'],
  // Transcribed from production on 2026-08-20, signed in, against capway
  // (`d8c56faf…`): 4,319 collections, six roots, eighteen levels deep, and
  // 4,316 of them carrying `toolId: null`. `basis` is a sentence the panel
  // prints verbatim, so it is guarded rather than treated as optional prose.
  sourceCollections: ['items', 'basis'],
  // Transcribed from production on 2026-08-16, signed in as an admin of the
  // Capway company: three buckets, every one granted, `revokedAt` null. Both
  // of these were UNOBSERVED until then - built from the contract's shape and
  // never seen answering - and both matched it exactly.
  contentReaderGrants: ['items'],
  reconstructionRequests: ['items'],
  syncDrift: ['items', 'nextCursor'],
  companyDashboard: [
    'companyId', 'observedAt', 'inventory', 'selectedRun', 'operationalReport',
    'sourceProvenanceGraph', 'attribution', 'entityReconstruction', 'commercialVerdict',
  ],
  analysisHistory: ['items', 'nextCursor'],
  analysisRun: ['run', 'tools', 'stage'],
  analysisEvents: ['runId', 'events', 'earliestRetainedSequence', 'retentionGap'],
  analysisLogs: ['runId', 'logs'],
  analysisReport: ['run', 'toolStates', 'observedAt', 'attribution', 'entityReconstruction', 'commercialVerdict'],
  reportCharts: ['companyId', 'runId', 'observedAt', 'items', 'truncated'],
  knowledgeGraph: ['companyId', 'runId', 'semantic', 'root', 'counts', 'observedAt', 'entityRelationshipGraph'],
  toolset: ['bucketId', 'attributionRunId', 'sealedAt', 'entries', 'stray', 'conservation', 'basis'],
  attributionObjects: ['bucketId', 'attributionRunId', 'sealedAt', 'items', 'nextCursor'],
  attributionStray: ['bucketId', 'attributionRunId', 'sealedAt', 'items', 'nextCursor'],
  attributionRules: ['items', 'registryState', 'basis'],
  // Verified against the pinned contract; the per-series payload key is
  // present only when availability is AVAILABLE, so it is not guarded.
  bucketChart: ['bucketId', 'series', 'availability'],
  walkthrough: ['bucketId', 'attributionRunId', 'sealedAt', 'tools', 'basis', 'recordPopulationBasis'],
  toolView: ['bucketId', 'tool', 'basis', 'recordPopulationBasis'],
  graphProjection: [
    'bucketId', 'lens', 'availability', 'gapCode', 'reason', 'graph', 'overview', 'nodes',
    'edges', 'aggregatedEdges', 'aggregation', 'bounds', 'scopedTool', 'nodesTruncated',
    'edgesTruncated', 'seedBasis', 'orderBasis', 'computedInMillis',
  ],
  search: ['bucketId', 'availability', 'items', 'windowCount', 'windowLimit', 'windowTruncated', 'scoreDerivation', 'indexBasis'],
  evidenceBatch: ['items', 'missing'],
  packagingRun: ['id', 'bucketId', 'state', 'factsRevision', 'requestedAt', 'stateChangedAt', 'deliveryRegion', 'supersededBy', 'supersededAt'],
};

/**
 * Routes whose success shape could not be observed yet, and why. Each one
 * needs data that does not exist in production today, so its guard is still
 * an assumption. They are listed rather than quietly trusted: the test
 * fails if a route is neither observed nor declared here, so a new guard
 * cannot be added on a guess without saying so.
 */
export const UNOBSERVED: Partial<Record<GuardedRoute, string>> = {
  knowledgeSubgraph: 'needs a knowledge-graph root; no run publishes one yet',
  toolRows: 'every walkthrough section reports UNSUPPORTED, so no rows are servable',
  graphNode: 'the graph projection reports NO_READY_GRAPH, so it exposes no node ids',
  record: 'entity publication is UNAVAILABLE, so no record reference resolves',
  finding: 'reconstruction is UNAVAILABLE, so no finding ids exist',
};
